/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useState } from 'react';
import type { UserDto, UserRole } from '@shared/types';
import { apiClient } from '../../services/apiClient';
import { AddUserModal } from './AddUserModal';
import { Tooltip } from '../layout/Tooltip';
import { useAuthStore } from '../../stores/authStore';

const ROLE_OPTIONS: { label: string; value: UserRole; tooltip: string }[] = [
  { label: 'No Role',      value: null,          tooltip: 'No access — user cannot use the application' },
  { label: 'Pharmacist',   value: 'Pharmacist',  tooltip: 'Browse formulas, assign taxonomy codes, and process batch queues' },
  { label: 'Manager',      value: 'Manager',     tooltip: 'Pharmacist access plus dashboard, audit trail, and productivity metrics' },
  { label: 'Admin',        value: 'Admin',        tooltip: 'Full access including user management' },
];

export function UserManagement() {
  const [users, setUsers]         = useState<UserDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [notice, setNotice]       = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const currentUserEmail = useAuthStore((s) => s.user?.email);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      const res = await apiClient.get<UserDto[]>('/users');
      setUsers(res.data);
    } catch {
      // handled by global error
    } finally {
      setLoading(false);
    }
  }

  async function handleRoleChange(userId: string, role: UserRole) {
    try {
      const res = await apiClient.put<UserDto>(`/users/${userId}/role`, { role });
      setUsers((prev) => prev.map((u) => (u.id === userId ? res.data : u)));
    } catch {
      // handled by global error
    }
  }

  async function handleToggleActive(userId: string, activate: boolean) {
    setTogglingId(userId);
    setNotice(null);
    try {
      await apiClient.put<UserDto>(`/users/${userId}/active`, { isActive: activate });
      setNotice({ message: activate ? 'User has been activated.' : 'User has been inactivated.', type: 'success' });
      await loadUsers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Action failed. Please try again.';
      setNotice({ message, type: 'error' });
    } finally {
      setTogglingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <Tooltip text="Search the directory and assign a role to a new user">
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 bg-dds-blue-500 hover:bg-dds-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add User
          </button>
        </Tooltip>
      </div>

      {notice && (
        <div className={`mb-4 flex items-center justify-between px-4 py-3 rounded-lg border text-sm font-medium ${
          notice.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <span>{notice.message}</span>
          <button onClick={() => setNotice(null)} className="ml-4 text-current opacity-60 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <Tooltip text="Role controls what sections of the app the user can access">Role</Tooltip>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => {
              const isSelf = currentUserEmail === user.email;
              const isToggling = togglingId === user.id;
              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.displayName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Tooltip
                      text={ROLE_OPTIONS.find((o) => o.value === user.role)?.tooltip ?? 'No access'}
                    >
                      <select
                        value={user.role ?? ''}
                        onChange={(e) => handleRoleChange(user.id, (e.target.value || null) as UserRole)}
                        className="text-sm border-gray-300 rounded-md shadow-sm focus:border-dds-blue-500 focus:ring-dds-blue-500"
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
                        ))}
                      </select>
                    </Tooltip>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Tooltip text={user.isActive ? 'User can sign in' : 'User cannot sign in'}>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </Tooltip>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isSelf ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <button
                        onClick={() => handleToggleActive(user.id, !user.isActive)}
                        disabled={isToggling}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          user.isActive
                            ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {isToggling ? 'Saving…' : user.isActive ? 'Inactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onAdded={(u) => {
            setUsers((prev) => [...prev.filter((x) => x.id !== u.id), u].sort((a, b) => a.email.localeCompare(b.email)));
            setShowAdd(false);
          }}
        />
      )}
    </div>
  );
}
